import React, { useState, useEffect } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ja from "date-fns/locale/ja";
import { MediaProvider } from "../../contexts/MediaContext";

// 日本語ロケールを登録
registerLocale("ja", ja);

function TrialListPageContent() {
  const [trialCustomers, setTrialCustomers] = useState([]); // 体験者リスト
  const [filteredTrialCustomers, setFilteredTrialCustomers] = useState([]); // フィルター済み体験者リスト
  const [studioNames, setStudioNames] = useState([]); // スタジオ名リスト
  const [studioLessons, setStudioLessons] = useState([]); // スタジオごとレッスンリスト
  const [filteredStudioLessons, setFilteredStudioLessons] = useState([]); // フィルター済みレッスンリスト
  const [resetHover, setResetHover] = useState(false);
  const [csvHover, setCsvHover] = useState(false);

  // フィルター・ソート状態
  const [filterVisible, setFilterVisible] = useState(false);
  const [filters, setFilters] = useState({
    date: "",
    createdAt: "",
    lesson: "",
    referralReason: "",
    studio: "",
    searchText: "",
  });
  const [sorts, setSorts] = useState({
    name: "", // 'asc' | 'desc' | ''
    age: "", // 'asc' | 'desc' | ''
  });

  // 本日日付をYYYY-MM-DD形式で取得する関数
  const getTodayString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // 体験顧客の取得
  const fetchTrialCustomers = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_trial_customers/`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const data = await response.json();

      setTrialCustomers(data);
    } catch (error) {
      console.error("データの取得中にエラーが発生しました:", error);
    }
  };

  // スタジオ名リストの取得
  const fetchStudioNames = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_studio_names/`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const data = await response.json();

      setStudioNames(data);
    } catch (error) {
      console.error("データの取得中にエラーが発生しました:", error);
    }
  };

  // スタジオごとレッスンリストの取得
  const fetchStudioLessons = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_studio_lessons/`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const data = await response.json();

      setStudioLessons(data);
    } catch (error) {
      console.error("データの取得中にエラーが発生しました:", error);
    }
  };

  // フィルター・ソート処理
  const applyFiltersAndSorts = () => {
    let result = [...trialCustomers];

    // 予約日フィルター（前方一致）
    if (filters.date) {
      result = result.filter((customer) =>
        customer.date.startsWith(filters.date)
      );
    }

    // 申込日フィルター（前方一致）
    if (filters.createdAt) {
      result = result.filter((customer) =>
        customer.created_at.startsWith(filters.createdAt)
      );
    }

    // レッスンフィルター（完全一致）
    if (filters.lesson) {
      result = result.filter((customer) => customer.lesson === filters.lesson);
    }

    // 知った理由フィルター（完全一致）
    if (filters.referralReason) {
      result = result.filter((customer) => customer.referral_reason === filters.referralReason);
    }

    // 店舗フィルター（完全一致）
    if (filters.studio) {
      result = result.filter((customer) => customer.studio === filters.studio);
    }

    // 検索テキスト（全カラム曖昧検索）
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      result = result.filter((customer) =>
        Object.values(customer).some((value) =>
          String(value).toLowerCase().includes(searchLower)
        )
      );
    }

    // 名前ソート
    if (sorts.name === "asc") {
      result.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sorts.name === "desc") {
      result.sort((a, b) => b.name.localeCompare(a.name));
    }

    // 年齢ソート
    if (sorts.age === "asc") {
      result.sort((a, b) => parseInt(a.age) - parseInt(b.age));
    } else if (sorts.age === "desc") {
      result.sort((a, b) => parseInt(b.age) - parseInt(a.age));
    }

    setFilteredTrialCustomers(result);
  };

  // 店舗変更時のレッスンフィルター処理
  const updateStudioLessons = (selectedStudio) => {
    if (!selectedStudio) {
      setFilteredStudioLessons(studioLessons);
      return;
    }

    const filteredLessons = {};
    if (studioLessons[selectedStudio]) {
      filteredLessons[selectedStudio] = studioLessons[selectedStudio];
    }
    setFilteredStudioLessons(filteredLessons);
  };

  // 日付をYYYY/MM/DD形式に変換する関数
  const formatDateToJapanese = (dateString) => {
    if (!dateString) return "";
    return dateString.replace(/-/g, "/");
  };

  // 日付をローカルタイムゾーンでYYYY-MM-DD形式に変換する関数
  const formatDateToLocal = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // 予約日変更処理
  const handleTrialDateChange = async (
    trialCustomerId,
    name,
    newDate,
    originalDate
  ) => {
    // 日付が変更されていない場合は何もしない
    if (newDate === originalDate) {
      return;
    }

    const formattedNewDate = formatDateToJapanese(newDate);
    const formattedOriginalDate = formatDateToJapanese(originalDate);

    if (
      !window.confirm(
        `${name} の予約日を変更してもよろしいですか？\n${formattedOriginalDate} → ${formattedNewDate}`
      )
    ) {
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/change_trial_date/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          trial_customer_id: trialCustomerId,
          new_date: newDate,
        }),
      });

      if (!response.ok) {
        throw new Error("予約日の変更に失敗しました。");
      }

      // 成功時、リストを再取得
      await fetchTrialCustomers();
    } catch (error) {
      console.error("予約日変更中にエラーが発生しました:", error);
      alert("予約日の変更に失敗しました。");
      // エラー時は元の日付に戻す
      await fetchTrialCustomers();
    }
  };

  // 体験者削除処理
  const handleDeleteTrialCustomer = async (trialCustomerId, name) => {
    if (
      !window.confirm(`${name} の体験予約をキャンセルしてもよろしいですか？`)
    ) {
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/cancel_trial_customer/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          trial_customer_id: trialCustomerId,
        }),
      });

      if (!response.ok) {
        throw new Error("削除に失敗しました。");
      }

      // 削除成功時、リストを再取得
      await fetchTrialCustomers();
    } catch (error) {
      console.error("削除中にエラーが発生しました:", error);
      alert("削除に失敗しました。");
    }
  };

  // CSVエクスポート処理
  const handleExportCsv = () => {
    if (!filteredTrialCustomers || filteredTrialCustomers.length === 0) {
      alert("出力対象のデータがありません。");
      return;
    }

    const headers = [
      "ID",
      "予約日",
      "名前",
      "ふりがな",
      "年齢",
      "店舗",
      "レッスン",
      "申込日",
      "知った理由",
      "メッセージ",
      "メール",
      "電話",
    ];

    // CSV用にエスケープ（ダブルクォート、カンマ、改行を含む場合は""で囲む）
    const escapeCsv = (value) => {
      if (value === null || value === undefined) return "";
      const str = String(value);
      if (/[",\r\n]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    // Excel等で先頭の0が消えないように、文字列として強制するための数式形式に変換
    const asExcelText = (value) => {
      if (value === null || value === undefined || value === "") return "";
      const str = String(value).replace(/"/g, '""');
      return `="${str}"`;
    };

    const rows = filteredTrialCustomers.map((c) => [
      asExcelText(String(c.id).padStart(4, "0")),
      formatDateToJapanese(c.date),
      c.name,
      c.name_kana,
      c.age,
      c.studio,
      c.lesson,
      c.created_at ? c.created_at.replace(/-/g, "/") : "",
      c.referral_reason,
      c.message,
      c.mail,
      asExcelText(c.tel),
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\r\n");

    // Excelでの文字化け防止のためBOM付きUTF-8
    const bom = "\uFEFF";
    const blob = new Blob([bom + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const today = getTodayString();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `trial_customers_${today}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // リセット処理
  const handleReset = () => {
    setFilters({
      date: "",
      createdAt: "",
      lesson: "",
      referralReason: "",
      studio: "",
      searchText: "",
    });
    setSorts({
      name: "",
      age: "",
    });
    setFilteredStudioLessons(studioLessons);
  };

  useEffect(() => {
    fetchTrialCustomers();
    fetchStudioNames();
    fetchStudioLessons();
  }, []);

  // 初期データ設定時の処理
  useEffect(() => {
    setFilteredTrialCustomers(trialCustomers);
  }, [trialCustomers]);

  useEffect(() => {
    setFilteredStudioLessons(studioLessons);
  }, [studioLessons]);

  // フィルター・ソート変更時の処理
  useEffect(() => {
    applyFiltersAndSorts();
  }, [filters, sorts, trialCustomers]);

  return (
    <>
      {/* ソートがonの時に.on */}
      <div className="buttonWrap">
        <button
          className={`filter ${filterVisible ? "on" : ""}`}
          onClick={() => setFilterVisible(!filterVisible)}
        >
          フィルター・絞り込み
        </button>
        <button
          className={`sort-reset${resetHover ? " on" : ""}`}
          onClick={handleReset}
          onMouseEnter={() => setResetHover(true)}
          onMouseLeave={() => setResetHover(false)}
        >
          リセット
        </button>
        <button
          className={`csv-export${csvHover ? " on" : ""}`}
          onClick={handleExportCsv}
          onMouseEnter={() => setCsvHover(true)}
          onMouseLeave={() => setCsvHover(false)}
        >
          CSV出力
        </button>
      </div>

      {/* .filter onで表示 */}
      <div className={`sort-setting ${filterVisible ? "on" : ""}`}>
        <dl>
          <dt>予約日</dt>
          <dd>
            <span onClick={() => document.getElementById('date-picker').focus()}>
              {filters.date ? formatDateToJapanese(filters.date) : "年/月/日"}
            </span>
            <DatePicker
              id="date-picker"
              selected={filters.date ? new Date(filters.date) : null}
              onChange={(date) => {
                if (date) {
                  const formattedDate = formatDateToLocal(date);
                  setFilters({ ...filters, date: formattedDate });
                } else {
                  setFilters({ ...filters, date: "" });
                }
              }}
              customInput={
                <i
                  className="fa fa-calendar"
                  style={{
                    left: "10px",
                    position: "relative",
                    cursor: "pointer",
                    fontSize: "13px",
                  }}
                />
              }
              dateFormat="yyyy/MM/dd"
              locale="ja"
            />
          </dd>
        </dl>
        <dl>
          <dt>名前</dt>
          <dd>
            <button
              className={sorts.name === "asc" ? "on" : ""}
              onClick={() =>
                setSorts({ ...sorts, name: sorts.name === "asc" ? "" : "asc" })
              }
            >
              昇順
            </button>
            <button
              className={sorts.name === "desc" ? "on" : ""}
              onClick={() =>
                setSorts({
                  ...sorts,
                  name: sorts.name === "desc" ? "" : "desc",
                })
              }
            >
              降順
            </button>
          </dd>
        </dl>
        <dl>
          <dt>年齢</dt>
          <dd>
            <button
              className={sorts.age === "asc" ? "on" : ""}
              onClick={() =>
                setSorts({ ...sorts, age: sorts.age === "asc" ? "" : "asc" })
              }
            >
              昇順
            </button>
            <button
              className={sorts.age === "desc" ? "on" : ""}
              onClick={() =>
                setSorts({ ...sorts, age: sorts.age === "desc" ? "" : "desc" })
              }
            >
              降順
            </button>
          </dd>
        </dl>
        <dl>
          <dt>店舗</dt>
          <dd>
            <select
              value={filters.studio}
              onChange={(e) => {
                const selectedStudio = e.target.value;
                setFilters({ ...filters, studio: selectedStudio, lesson: "" });
                updateStudioLessons(selectedStudio);
              }}
            >
              <option value="">選択してください</option>
              {studioNames?.map((studioName, index) => (
                <option key={index} value={studioName}>
                  {studioName}
                </option>
              ))}
            </select>
          </dd>
        </dl>
        <dl>
          <dt>レッスン</dt>
          <dd>
            <select
              value={filters.lesson}
              onChange={(e) =>
                setFilters({ ...filters, lesson: e.target.value })
              }
            >
              <option value="">選択してください</option>
              {filteredStudioLessons &&
                Object.entries(filteredStudioLessons).map(
                  ([studioName, lessons]) => (
                    <optgroup key={studioName} label={studioName}>
                      {lessons.map((lesson, idx) => (
                        <option key={studioName + "-" + idx} value={lesson}>
                          {lesson}
                        </option>
                      ))}
                    </optgroup>
                  )
                )}
            </select>
          </dd>
        </dl>
        <dl>
          <dt>申込日</dt>
          <dd>
            <span onClick={() => document.getElementById('created-date-picker').focus()}>
              {filters.createdAt ? formatDateToJapanese(filters.createdAt) : "年/月/日"}
            </span>
            <DatePicker
              id="created-date-picker"
              selected={filters.createdAt ? new Date(filters.createdAt) : null}
              onChange={(date) => {
                if (date) {
                  const formattedDate = formatDateToLocal(date);
                  setFilters({ ...filters, createdAt: formattedDate });
                } else {
                  setFilters({ ...filters, createdAt: "" });
                }
              }}
              customInput={
                <i
                  className="fa fa-calendar"
                  style={{
                    left: "10px",
                    position: "relative",
                    cursor: "pointer",
                    fontSize: "13px",
                  }}
                />
              }
              dateFormat="yyyy/MM/dd"
              locale="ja"
            />
          </dd>
        </dl>
        <dl>
          <dt>知った理由</dt>
          <dd>
            <button
              className={filters.referralReason === "SNS広告" ? "on" : ""}
              onClick={() =>
                setFilters({
                  ...filters,
                  referralReason: filters.referralReason === "SNS広告" ? "" : "SNS広告",
                })
              }
            >
              SNS広告
            </button>
            <button
              className={filters.referralReason === "イベントで見た" ? "on" : ""}
              onClick={() =>
                setFilters({
                  ...filters,
                  referralReason: filters.referralReason === "イベントで見た" ? "" : "イベントで見た",
                })
              }
            >
              イベントで見た
            </button>
            <button
              className={filters.referralReason === "現会員" ? "on" : ""}
              onClick={() =>
                setFilters({
                  ...filters,
                  referralReason: filters.referralReason === "現会員" ? "" : "現会員",
                })
              }
            >
              現会員
            </button>
          </dd>
        </dl>
        <dl className="saerch">
          <dt>
            <i className="fa-thin fa-magnifying-glass"></i>
          </dt>
          <dd>
            <input
              type="text"
              placeholder="検索..."
              value={filters.searchText}
              onChange={(e) =>
                setFilters({ ...filters, searchText: e.target.value })
              }
            />
          </dd>
        </dl>
      </div>

      {/* 表 */}
      <div className="dataBoxWrap">
        <div className="dataBox">
          <ul>
            <li className="ttl sort-on">
              <p>ID</p>
            </li>
            <li className="ttl">
              <p>予約日</p>
            </li>
            <li className="ttl">
              <p>名前</p>
            </li>
            <li className="ttl">
              <p>ふりがな</p>
            </li>
            <li className="ttl">
              <p>年齢</p>
            </li>
            <li className="ttl">
              <p>店舗</p>
            </li>
            <li className="ttl">
              <p>レッスン</p>
            </li>
            <li className="ttl">
              <p>申込日</p>
            </li>
            <li className="ttl">
              <p>知った理由</p>
            </li>
            <li className="ttl">
              <p>メッセージ</p>
            </li>
            <li className="ttl">
              <p>メール</p>
            </li>
            <li className="ttl">
              <p>電話</p>
            </li>
            <li className="ttl">
              <p>削除</p>
            </li>
          </ul>

          {/* 体験リスト */}
          {filteredTrialCustomers?.map((trialCustomer) => (
            <ul
              key={trialCustomer.id}
              className={trialCustomer.date === getTodayString() ? "today" : ""}
            >
              {/* ID */}
              <li>{String(trialCustomer.id).padStart(4, "0")}</li>
              {/* 予約日 */}
              <li>
                {formatDateToJapanese(trialCustomer.date)}
                <DatePicker
                  selected={
                    trialCustomer.date ? new Date(trialCustomer.date) : null
                  }
                  onChange={(date) => {
                    if (date) {
                      const formattedDate = formatDateToLocal(date);
                      handleTrialDateChange(
                        trialCustomer.id,
                        trialCustomer.name,
                        formattedDate,
                        trialCustomer.date
                      );
                    }
                  }}
                  customInput={
                    <i
                      className="fa fa-calendar"
                      style={{
                        left: "10px",
                        position: "relative",
                        cursor: "pointer",
                        fontSize: "13px",
                      }}
                    />
                  }
                  dateFormat="yyyy/MM/dd"
                  locale="ja"
                />
              </li>
              {/* 名前 */}
              <li>{trialCustomer.name}</li>
              {/* ふりがな */}
              <li>{trialCustomer.name_kana}</li>
              {/* 年齢 */}
              <li>{trialCustomer.age}</li>
              {/* 店舗 */}
              <li>{trialCustomer.studio}</li>
              {/* レッスン */}
              <li>{trialCustomer.lesson}</li>
              {/* 申込日 */}
              <li>{trialCustomer.created_at.replace(/-/g, "/")}</li>
              {/* 知った理由 */}
              <li>{trialCustomer.referral_reason}</li>
              {/* メッセージ */}
              <li>{trialCustomer.message}</li>
              {/* メール */}
              <li className="icon-area">
                <a href={`mailto:${trialCustomer.mail}`}>
                  <i className="fa fa-envelope" aria-label="メール"></i>
                </a>
              </li>
              {/* 電話 */}
              <li className="icon-area">
                <a href={`tel:${trialCustomer.tel}`}>
                  <i className="fa fa-phone" aria-label="電話"></i>
                </a>
              </li>
              <li className="icon-area">
                <i
                  className="fa fa-minus"
                  aria-label="削除"
                  onClick={() =>
                    handleDeleteTrialCustomer(
                      trialCustomer.id,
                      trialCustomer.name
                    )
                  }
                  style={{ cursor: "pointer" }}
                ></i>
              </li>
            </ul>
          ))}
        </div>
      </div>
    </>
  );
}

export default function TrialListPage() {
  return (
    <MediaProvider>
      <TrialListPageContent />
    </MediaProvider>
  );
}
